import { useEffect, useRef, useState, useId } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, CheckCircle2, AlertTriangle, ScanLine } from "lucide-react";

// Scanner de código de barras — "Aceite de Responsabilidade" (fev/2026).
//
// Sensibilidade iOS Safari 18.7 (fix v2, 12/fev/2026):
//   ANTES o useEffect dependia de [open, onScan]. Cada render do
//   MobileEntregador criava uma nova função onScan (closure sobre `items`),
//   então o effect reiniciava a câmera a cada state update → pipeline de
//   vídeo do iOS crashava depois de alguns start/stop rápidos.
//
//   FIX: onScan vai pra `onScanRef` (atualizada sem reiniciar o effect),
//        o effect só depende de [open]. Câmera abre 1x, fecha 1x.
//        + cleanup força esvaziar o container DOM (html5-qrcode às vezes
//        deixa <video> fantasma no iOS após stop()).
//        + ID único por instância (useId) — impede colisão se o dialog
//        for remontado antes do stop() terminar.

export function ConferenciaBarcodeDialog({ open, onOpenChange, onScan }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const containerId = `conferencia-barcode-${uid}`;
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ text: "", ts: 0 });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null); // { kind, message }
  const flashTimerRef = useRef(null);

  // Ref pra callback — sem isso o effect reiniciaria a câmera a cada
  // mudança de items no MobileEntregador (bug iOS 18.7 fev/2026).
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    if (!open) return;
    setReady(false);
    setError(null);
    setFlash(null);
    let cancelled = false;
    let scanner = null;

    const cleanup = () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = null;
      }
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        // s.stop() é async — usamos Promise.resolve pra normalizar caso
        // a lib retorne sync em alguma versão futura.
        Promise.resolve()
          .then(() => s.stop())
          .catch((stopErr) => {
            // já parado / erro benigno — log apenas em debug pra rastreabilidade iOS 18.7
            if (stopErr) console.debug("[conferencia-scan] stop benigno:", stopErr);
          })
          .finally(() => {
            try {
              s.clear();
            } catch (clearErr) {
              // clear() já não aplica em algumas versões do html5-qrcode
              if (clearErr) console.debug("[conferencia-scan] clear benigno:", clearErr);
            }
            // html5-qrcode às vezes deixa <video> fantasma no container
            // em iOS Safari. Força limpeza pra próxima abertura ser limpa.
            // Usa replaceChildren() em vez de innerHTML="" — evita false-positive
            // de XSS no linter (não aceita string como fonte de HTML).
            const el = document.getElementById(containerId);
            if (el) el.replaceChildren();
          });
      }
    };

    (async () => {
      let mod;
      try {
        mod = await import("html5-qrcode");
      } catch (err) {
        console.warn("[conferencia-scan] falha ao carregar html5-qrcode:", err);
        if (!cancelled) setError("Falha ao carregar biblioteca de scanner.");
        return;
      }
      if (cancelled) return;
      const { Html5Qrcode } = mod;
      try {
        scanner = new Html5Qrcode(containerId, /* verbose */ false);
      } catch (err) {
        console.warn("[conferencia-scan] falha ao instanciar Html5Qrcode:", err);
        if (!cancelled) setError("Falha ao inicializar o scanner.");
        return;
      }
      scannerRef.current = scanner;
      const config = {
        fps: 8,  // iOS Safari respira melhor com fps menor
        qrbox: (w, h) => ({
          width: Math.min(Math.max(w - 24, 200), 320),
          height: Math.min(Math.max(Math.floor(h * 0.45), 120), 200),
        }),
        aspectRatio: 1.6,
      };
      try {
        await scanner.start(
          { facingMode: "environment" },
          config,
          (decodedText) => {
            if (cancelled) return;
            const now = Date.now();
            // Debounce: mesma leitura em <2.5s é ignorada (evita spam POST)
            if (decodedText === lastScanRef.current.text && now - lastScanRef.current.ts < 2500) return;
            lastScanRef.current = { text: decodedText, ts: now };
            let result = { kind: "not_in_list" };
            try {
              result = onScanRef.current?.(decodedText) || result;
            } catch (err) {
              console.error("[conferencia-scan] onScan handler threw:", err);
            }
            const flashMap = {
              match: {
                kind: result.all_done === false ? "partial" : "success",
                message: result.all_done === false
                  ? `Pedido #${result.pedido || decodedText} conferido (${result.done}/${result.total}) — falta ${result.total - result.done}`
                  : `Pedido #${result.pedido || decodedText} confirmado — Em Trânsito`,
              },
              pedido_ja_escaneado: { kind: "info",    message: `Pedido #${result.pedido || decodedText} já foi escaneado (${result.done}/${result.total})` },
              already_confirmed:   { kind: "info",    message: `Pedido #${result.pedido || decodedText} já foi confirmado` },
              not_in_list:         { kind: "error",   message: "Este pedido não foi atribuído a você" },
              empty:               { kind: "error",   message: "Código vazio — tente novamente" },
            };
            if (cancelled) return;
            setFlash(flashMap[result.kind] || flashMap.not_in_list);
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
            flashTimerRef.current = setTimeout(() => {
              if (!cancelled) setFlash(null);
            }, 2400);
          },
          () => { /* qrCodeErrorCallback ruidoso — ignoramos */ }
        );
        if (!cancelled) setReady(true);
      } catch (err) {
        if (!cancelled) {
          const msg = err?.message || String(err || "Câmera indisponível.");
          setError(msg.includes("NotAllowed")
            ? "Permissão de câmera negada. Ative nas configurações do navegador."
            : msg);
        }
      }
    })();

    return () => { cancelled = true; cleanup(); };
  }, [open, containerId]);  // ⚠️ NÃO adicionar onScan aqui — usa ref pra evitar restart

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="conferencia-scan-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-violet-800">
            <Camera className="w-5 h-5" />
            Escanear código de barras
          </DialogTitle>
          <DialogDescription className="text-sm">
            Aponte a câmera para o código de barras do pedido. Pode escanear vários pacotes seguidos.
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-black min-h-[280px] flex items-center justify-center overflow-hidden">
          <div id={containerId} className="w-full min-h-[280px]" />
          {!ready && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white/90 text-sm gap-2 pointer-events-none" data-testid="conferencia-scan-loading">
              <ScanLine className="w-5 h-5 animate-pulse" />
              Abrindo câmera...
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm p-4 text-center bg-black/70" data-testid="conferencia-scan-error">
              <div>
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <div className="font-bold">Câmera indisponível</div>
                <div className="text-xs opacity-80 mt-1">{error}</div>
                <div className="text-xs opacity-70 mt-2">Você ainda pode digitar o número na busca.</div>
              </div>
            </div>
          )}
          {flash && (
            <div
              className={`absolute inset-x-0 top-0 px-3 py-2.5 text-center text-sm font-extrabold text-white flex items-center justify-center gap-2 shadow-lg ${
                flash.kind === "success"
                  ? "bg-emerald-600"
                  : flash.kind === "partial"
                    ? "bg-sky-600"
                    : flash.kind === "info"
                      ? "bg-amber-500"
                      : "bg-red-600"
              }`}
              data-testid={`conferencia-scan-flash-${flash.kind}`}
            >
              {flash.kind === "success" || flash.kind === "partial"
                ? <CheckCircle2 className="w-5 h-5" />
                : <AlertTriangle className="w-5 h-5" />}
              <span className="truncate">{flash.message}</span>
            </div>
          )}
        </div>

        <div className="p-4 pt-3 space-y-2">
          <p className="text-xs text-muted-foreground text-center">
            Sufixos como <span className="mono">0000</span> ou dígitos extras são ignorados —
            a busca casa por prefixo/substring.
          </p>
          <Button
            className="w-full h-11"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="btn-conferencia-scan-close"
          >
            <X className="w-4 h-4 mr-2" />Fechar scanner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
